import { Fragment, MutableRefObject, ReactNode, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export interface ModalProps {
  open: boolean;
  isFullScreen?: boolean;
  setOpen: (val: boolean) => void;
  children: ReactNode;
  panelClassName?: string;
  disableCloseOnOutsideClick?: boolean;
  disableRoundedCorners?: boolean;
  /**
   * 'top' keeps the panel's top edge fixed, so content that grows or shrinks
   * below doesn't slide the fields above it around.
   */
  verticalAlign?: 'center' | 'top';
  /**
   * The element to focus when the modal opens. Without one the panel itself
   * takes focus, so a modal whose first job is typing should point this at
   * its field.
   */
  initialFocus?: MutableRefObject<HTMLElement | null>;
}

const modalPanelCva = cva(
  'transform overflow-hidden bg-white text-left shadow-xl transition-all',
  {
    variants: {
      rounded: {
        true: 'rounded-lg',
        false: '',
      },
      fullScreen: {
        true: 'fixed w-full h-screen',
        false: '',
      },
    },
    defaultVariants: {
      rounded: true,
      fullScreen: false,
    },
  }
);

export function Modal({
  open,
  isFullScreen,
  setOpen,
  children,
  panelClassName = 'relative',
  disableCloseOnOutsideClick,
  disableRoundedCorners = false,
  verticalAlign = 'center',
  initialFocus,
}: ModalProps) {
  const panelFocusRef = useRef(null);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={initialFocus ?? panelFocusRef}
        onClose={() => (disableCloseOnOutsideClick ? null : setOpen(false))}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-100 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className={twMerge(
              'flex min-h-full items-end justify-center text-center sm:items-center sm:p-0',
              verticalAlign === 'top' &&
                'items-start sm:items-start pt-10 sm:pt-16'
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  modalPanelCva({
                    rounded: !disableRoundedCorners && !isFullScreen,
                    fullScreen: isFullScreen,
                  }),
                  panelClassName
                )}
              >
                <div className="w-full px-4 pb-4 pt-5 sm:p-6">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default Modal;
